import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  Eye,
  FileText,
  GraduationCap,
  Pencil,
  Plus,
  Save,
  Search,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { formatBRL, formatPct } from "../../utils/format";

const EMPTY_SERVICE = {
  sigla: "",
  descricao: "",
  nome_proposta: "",
  norma_referencia: "",
  categoria: "servico",
  custo_padrao: 0,
  venda_padrao: 0,
};

const CATEGORY_OPTIONS = [
  { value: "servico", label: "Serviço técnico", help: "Vai para o bloco geral de serviços da proposta." },
  { value: "programa", label: "Programa / documento", help: "Programas e documentos entram no bloco geral de serviços." },
  { value: "laudo", label: "Laudo / relatório", help: "Laudos e relatórios entram no bloco geral de serviços." },
  { value: "treinamento", label: "Treinamento / capacitação", help: "Vai para o bloco de treinamentos e habilita carga horária na proposta." },
  { value: "avaliacao", label: "Avaliação / inspeção", help: "Avaliações e inspeções deste catálogo ficam no bloco geral. Avaliações quantitativas recorrentes podem usar o catálogo de Avaliações fixas." },
  { value: "consultoria", label: "Consultoria / acompanhamento", help: "Consultorias e acompanhamentos entram no bloco geral de serviços." },
  { value: "visita", label: "Visita", help: "Serviços realizados por visita mostram quantidade e valor por visita na proposta." },
  { value: "projeto", label: "Projeto / adequação", help: "Projetos e adequações entram no bloco geral de serviços." },
  { value: "outro", label: "Outro", help: "Use apenas quando nenhum grupo específico representar o serviço." },
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORY_OPTIONS.map((item) => [item.value, item.label]));
const proposalDestination = (category) => category === "treinamento" ? "Treinamentos e capacitações" : "Serviços, documentos, laudos e projetos";

const markupPct = (custo, venda) => {
  const c = Number(custo || 0);
  const v = Number(venda || 0);
  return c > 0 ? ((v - c) / c) * 100 : 0;
};

const normalizeItem = (item) => ({
  ...EMPTY_SERVICE,
  ...item,
  nome_proposta: String(item?.nome_proposta || item?.descricao || item?.sigla || ""),
  categoria: item?.categoria || "servico",
});

const comparable = (item) => JSON.stringify({
  sigla: String(item?.sigla || "").trim(),
  descricao: String(item?.descricao || "").trim(),
  nome_proposta: String(item?.nome_proposta || "").trim(),
  norma_referencia: String(item?.norma_referencia || "").trim(),
  categoria: item?.categoria || "servico",
  custo_padrao: Number(item?.custo_padrao || 0),
  venda_padrao: Number(item?.venda_padrao || 0),
});

function ServiceEditor({ value, onChange }) {
  const setField = (key, next) => onChange({ ...value, [key]: next });
  const updateInternalName = (next) => {
    const previous = String(value.descricao || "");
    const commercial = String(value.nome_proposta || "");
    onChange({
      ...value,
      descricao: next,
      nome_proposta: !commercial || commercial === previous ? next : commercial,
    });
  };
  const selectedCategory = CATEGORY_OPTIONS.find((x) => x.value === value.categoria) || CATEGORY_OPTIONS[0];
  const preview = String(value.nome_proposta || value.descricao || "").trim();

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,.75fr)]">
      <div className="space-y-5">
        <section className="rounded-xl border border-border bg-background p-4 sm:p-5">
          <div className="mb-4">
            <div className="text-sm font-semibold">Identificação do serviço</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Comece pelo nome. Código, norma e grupo organizam o catálogo e definem onde o item é configurado na proposta.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Nome do serviço *</Label>
              <Input
                className="mt-1.5"
                value={value.descricao}
                onChange={(e) => updateInternalName(e.target.value)}
                placeholder="Ex.: Acompanhamento mensal de SST"
                autoFocus
                data-testid="service-editor-name"
              />
            </div>
            <div>
              <Label>Grupo interno</Label>
              <Select value={value.categoria} onValueChange={(next) => setField("categoria", next)}>
                <SelectTrigger className="mt-1.5" data-testid="service-editor-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{selectedCategory.help} <strong>O grupo não reescreve o texto comercial.</strong></p>
              <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${value.categoria === "treinamento" ? "border-amber-500/30 bg-amber-500/[0.06] text-amber-800 dark:text-amber-300" : "border-border bg-muted/20 text-muted-foreground"}`}>
                Destino no configurador: <strong>{proposalDestination(value.categoria)}</strong>
              </div>
            </div>
            <div>
              <Label>Código / sigla <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <Input className="mt-1.5 font-mono" value={value.sigla} onChange={(e) => setField("sigla", e.target.value)} placeholder="Ex.: LTCAT, NR-35" />
            </div>
            <div className="md:col-span-2">
              <Label>Norma / referência <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <Input className="mt-1.5" value={value.norma_referencia} onChange={(e) => setField("norma_referencia", e.target.value)} placeholder="Ex.: NR-12, Lei 8.213/91 ou referência contratual" />
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-primary/20 bg-primary/[0.025] p-4 sm:p-5">
          <div className="mb-4 flex items-start gap-3">
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Eye className="size-4" /></div>
            <div>
              <div className="text-sm font-semibold">Como aparece para o cliente</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Este campo é usado literalmente no site e na proposta. Não são adicionados prefixos automáticos como “Treinamento de” ou “Desenvolvimento do”.</p>
            </div>
          </div>
          <Label>Nome exibido na proposta *</Label>
          <Textarea
            className="mt-1.5 min-h-[82px] resize-y"
            value={value.nome_proposta}
            onChange={(e) => setField("nome_proposta", e.target.value)}
            placeholder="Ex.: Acompanhamento mensal de Saúde e Segurança do Trabalho"
            data-testid="service-editor-commercial-name"
          />
          <div className="mt-3 rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Prévia na proposta</div>
            <div className="mt-1.5 text-sm font-semibold">{preview || "Digite o nome comercial acima"}</div>
          </div>
        </section>
      </div>

      <section className="h-fit rounded-xl border border-border bg-background p-4 sm:p-5 xl:sticky xl:top-5">
        <div className="mb-4">
          <div className="text-sm font-semibold">Preço padrão</div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">A formação de preço fica na mesma ficha, mas separada do texto comercial.</p>
        </div>
        <div className="space-y-4">
          <div><Label>Custo padrão</Label><Input className="mt-1.5" type="number" min="0" step="0.01" value={value.custo_padrao} onChange={(e) => setField("custo_padrao", Number(e.target.value || 0))} /></div>
          <div><Label>Preço de venda padrão</Label><Input className="mt-1.5" type="number" min="0" step="0.01" value={value.venda_padrao} onChange={(e) => setField("venda_padrao", Number(e.target.value || 0))} /></div>
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/35 p-3 text-center">
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Custo</div><div className="mt-1 text-xs font-semibold">{formatBRL(value.custo_padrao)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Venda</div><div className="mt-1 text-xs font-semibold">{formatBRL(value.venda_padrao)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Markup</div><div className="mt-1 text-xs font-semibold">{formatPct(markupPct(value.custo_padrao, value.venda_padrao))}</div></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function Services() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [editorMode, setEditorMode] = useState("");
  const [editingId, setEditingId] = useState("");
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = async () => {
    try {
      const rows = (await api.get("/services")).data || [];
      setItems(rows.map(normalizeItem));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar serviços e preços");
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = !!draft && comparable(draft) !== comparable(baseline || EMPTY_SERVICE);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("pt-BR");
    return items.filter((it) => {
      if (category !== "all" && it.categoria !== category) return false;
      if (!needle) return true;
      return [it.sigla, it.descricao, it.nome_proposta, it.norma_referencia, CATEGORY_LABELS[it.categoria], proposalDestination(it.categoria)]
        .some((value) => String(value || "").toLocaleLowerCase("pt-BR").includes(needle));
    });
  }, [items, query, category]);

  const summary = useMemo(() => ({
    total: items.length,
    documentos: items.filter((x) => ["programa", "laudo"].includes(x.categoria)).length,
    treinamentos: items.filter((x) => x.categoria === "treinamento").length,
    flexiveis: items.filter((x) => ["servico", "consultoria", "avaliacao", "visita", "projeto", "outro"].includes(x.categoria)).length,
  }), [items]);

  const validate = (it) => {
    const description = String(it?.descricao || "").trim();
    const commercial = String(it?.nome_proposta || "").trim();
    const sigla = String(it?.sigla || "").trim().toLocaleLowerCase("pt-BR");
    if (!description) return "Informe o nome do serviço.";
    if (!commercial) return "Informe como o serviço deve aparecer na proposta.";
    if (Number(it?.custo_padrao || 0) < 0 || Number(it?.venda_padrao || 0) < 0) return "Custo e venda não podem ser negativos.";
    if (items.some((row) => row.id !== editingId && String(row.descricao || "").trim().toLocaleLowerCase("pt-BR") === description.toLocaleLowerCase("pt-BR"))) return "Já existe um serviço com este nome interno.";
    if (sigla && items.some((row) => row.id !== editingId && String(row.sigla || "").trim().toLocaleLowerCase("pt-BR") === sigla)) return "Já existe um serviço com esta sigla.";
    return "";
  };

  const payloadOf = (it) => ({
    sigla: String(it.sigla || "").trim(),
    descricao: String(it.descricao || "").trim(),
    nome_proposta: String(it.nome_proposta || it.descricao || "").trim(),
    norma_referencia: String(it.norma_referencia || "").trim(),
    custo_padrao: Number(it.custo_padrao || 0),
    venda_padrao: Number(it.venda_padrao || 0),
    categoria: it.categoria || "servico",
  });

  const requireCleanWorkspace = () => {
    if (!dirty) return true;
    toast.error("Há alterações não salvas. Salve ou descarte a ficha atual antes de abrir outro serviço.");
    return false;
  };

  const startNew = () => {
    if (!requireCleanWorkspace()) return;
    const fresh = normalizeItem(EMPTY_SERVICE);
    setEditorMode("new");
    setEditingId("");
    setDraft(fresh);
    setBaseline(fresh);
    setDeleteConfirm("");
  };

  const startEdit = (item) => {
    if (editingId === item.id && editorMode === "edit") return;
    if (!requireCleanWorkspace()) return;
    const normalized = normalizeItem(item);
    setEditorMode("edit");
    setEditingId(item.id);
    setDraft(normalized);
    setBaseline(normalized);
    setDeleteConfirm("");
  };

  const closeEditor = () => {
    if (dirty) {
      toast.error("Há alterações não salvas. Use “Descartar alterações” se quiser sair sem salvar.");
      return;
    }
    setEditorMode("");
    setEditingId("");
    setDraft(null);
    setBaseline(null);
  };

  const discard = () => {
    setEditorMode("");
    setEditingId("");
    setDraft(null);
    setBaseline(null);
    toast("Alterações descartadas");
  };

  const saveWorkspace = async () => {
    if (!draft) return;
    const error = validate(draft);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      if (editorMode === "new") {
        const r = await api.post("/services", payloadOf(draft));
        const saved = normalizeItem(r.data);
        setItems((current) => [...current, saved].sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR")));
        toast.success("Serviço criado e disponível nas propostas");
      } else if (editorMode === "edit" && editingId) {
        const r = await api.put(`/services/${editingId}`, payloadOf(draft));
        const saved = normalizeItem(r.data);
        setItems((current) => current.map((row) => row.id === editingId ? saved : row));
        toast.success("Alterações do serviço salvas");
      }
      setEditorMode("");
      setEditingId("");
      setDraft(null);
      setBaseline(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar o serviço");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (dirty) {
      toast.error("Salve ou descarte a ficha em edição antes de excluir outro serviço.");
      return;
    }
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }
    try {
      await api.delete(`/services/${id}`);
      setItems((current) => current.filter((it) => it.id !== id));
      setDeleteConfirm("");
      if (editingId === id) discard();
      toast.success("Serviço excluído");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir serviço");
    }
  };

  const Metric = ({ value, label, icon: Icon }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="size-4 text-primary" /> {label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );

  return (
    <>
      <Header
        title="Serviços e preços"
        subtitle="Ficha completa do serviço, com destino no configurador e texto comercial independentes"
        actions={(
          <Button onClick={startNew} disabled={editorMode === "new"} data-testid="add-service-btn">
            <Plus className="size-4 mr-1" /> Novo serviço
          </Button>
        )}
      />

      <div className="mx-auto w-full max-w-[1500px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="services-page">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric value={summary.total} label="Itens no catálogo" icon={FileText} />
          <Metric value={summary.documentos} label="Programas e laudos" icon={FileText} />
          <Metric value={summary.treinamentos} label="Treinamentos" icon={GraduationCap} />
          <Metric value={summary.flexiveis} label="Serviços flexíveis" icon={Tag} />
        </div>

        <div className="rounded-2xl border border-primary/20 bg-primary/[0.035] p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Check className="size-4" /></div>
            <div>
              <div className="font-medium">Uma ficha por serviço, um único salvamento</div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Não existe edição por linha. Avisos de alterações pendentes ficam dentro do sistema. O grupo define o bloco do configurador; o campo “Nome exibido na proposta” define somente o texto para o cliente.</p>
            </div>
          </div>
        </div>

        {editorMode && draft && (
          <section className="rounded-2xl border border-primary/30 bg-card shadow-sm" data-testid="service-workspace">
            <div className="flex flex-col gap-3 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-primary">{editorMode === "new" ? "Novo serviço" : "Editando serviço"}</div>
                <h2 className="mt-1 text-lg font-semibold">{editorMode === "new" ? "Preencha a ficha do serviço" : (baseline?.descricao || "Serviço")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">Todas as alterações desta ficha são salvas juntas.</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={closeEditor} disabled={dirty}><X className="size-4 mr-1" /> Fechar ficha</Button>
            </div>

            <div className="p-5 sm:p-6"><ServiceEditor value={draft} onChange={setDraft} /></div>

            <div className={`sticky bottom-0 z-10 flex flex-col gap-3 border-t p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-6 ${dirty ? "border-amber-500/25 bg-background/95" : "border-border bg-background/95"}`}>
              <div className="flex items-center gap-2 text-sm">
                {dirty ? <><AlertTriangle className="size-4 text-amber-600" /><span className="font-medium text-amber-700 dark:text-amber-400">Alterações ainda não salvas</span></> : <><Check className="size-4 text-emerald-600" /><span className="text-muted-foreground">Nenhuma alteração pendente</span></>}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button type="button" variant="outline" onClick={dirty ? discard : closeEditor}>{dirty ? "Descartar alterações" : "Cancelar"}</Button>
                <Button type="button" onClick={saveWorkspace} disabled={saving || !dirty} data-testid="service-workspace-save"><Save className="size-4 mr-1" /> {saving ? "Salvando..." : editorMode === "new" ? "Criar serviço" : "Salvar alterações"}</Button>
              </div>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-border bg-card p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-0 flex-1 lg:max-w-2xl"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar pelo nome, texto da proposta, código, norma ou destino..." /></div>
            <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-full lg:w-64"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos os grupos</SelectItem>{CATEGORY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select>
          </div>
        </section>

        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((it) => {
            const active = editorMode === "edit" && editingId === it.id;
            const confirming = deleteConfirm === it.id;
            return (
              <article key={it.id} className={`rounded-2xl border bg-card p-4 sm:p-5 ${active ? "border-primary/45 ring-1 ring-primary/10" : "border-border"}`} data-testid={`service-row-${it.id}`}>
                <div className="flex h-full flex-col gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><span className="rounded-md bg-muted px-2 py-1 text-[11px] font-medium">{CATEGORY_LABELS[it.categoria] || "Serviço"}</span>{it.sigla && <span className="font-mono text-xs text-muted-foreground">{it.sigla}</span>}<span className={`rounded-md px-2 py-1 text-[11px] ${it.categoria === "treinamento" ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-primary/10 text-primary"}`}>{proposalDestination(it.categoria)}</span></div>
                    <div className="mt-2 text-base font-semibold">{it.nome_proposta || it.descricao}</div>
                    {it.descricao !== it.nome_proposta && <div className="mt-1 text-sm text-muted-foreground">Cadastro interno: {it.descricao}</div>}
                    {it.norma_referencia && <div className="mt-1 text-xs text-muted-foreground">Referência: {it.norma_referencia}</div>}
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"><span>Custo {formatBRL(it.custo_padrao)}</span><span>Venda {formatBRL(it.venda_padrao)}</span><span>Markup {formatPct(markupPct(it.custo_padrao, it.venda_padrao))}</span></div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                    <Button variant={active ? "secondary" : "outline"} size="sm" onClick={() => startEdit(it)} disabled={active}><Pencil className="size-4 mr-1" /> {active ? "Em edição" : "Abrir ficha"}</Button>
                    <Button variant={confirming ? "destructive" : "ghost"} size="sm" onClick={() => del(it.id)}>{confirming ? "Confirmar exclusão" : <><Trash2 className="size-4 mr-1 text-destructive" /> Excluir</>}</Button>
                    {confirming && <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm("")}>Cancelar</Button>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Nenhum item corresponde aos filtros.</div>}
      </div>
    </>
  );
}
